import React, { useCallback, useMemo } from 'react';

import {
  Icon,
  icons,
  Button,
  Flex,
  Box,
  Colors,
} from '@tapresearch/ui/components';

import { Buttons } from 'Shared/Button';
export interface PaginationProps {
  currentPage: number;
  totalCount: number;
  pageCount: number;
  perPage: number;
  maxPagesToShow: number;

  onPageChange?: (page: number) => void;
}

type PaginationButtonProps = React.PropsWithChildren<{
  page?: number;
  onClick?: (page: number) => void;
  navButton?: boolean;
  disabled?: boolean;
  active?: boolean;
  'data-testid'?: string;
}>;

const activeColor = Colors.blue[500];

const paginationButtonStyles = ({ active, onClick }: PaginationButtonProps) => {
  return {
    outline: 'none',
    bg: active ? activeColor : Colors.white,
    border: !active ? '2px solid' : 'none',
    borderColor: Colors.gray[200],
    color: active ? Colors.white : Colors.black,
    pointerEvents: (onClick ? 'all' : 'none') as 'all' | 'none',
    boxShadow: 'none',
    minWidth: '32px',
    height: 8,
    padding: 1,
    variant: 'subtle',
    _hover: {
      bg: active ? activeColor : Colors.blue[100],
    },
  };
};

const paginationNavButtonStyles = (props: PaginationButtonProps) => {
  return {
    ...paginationButtonStyles(props),
    ...Buttons.SecondarySmall,
    height: 8,
    _disabled: {
      ...Buttons.SecondarySmall['_disabled'],
      bg: 'gray.700',
      color: 'gray.100',
      _hover: {
        bg: 'gray.700',
        color: 'gray.100',
      },
    },
  };
};

function PaginationButton(props: PaginationButtonProps) {
  const {
    page,
    onClick,
    children,
    disabled = false,
    navButton,
    'data-testid': testId,
    active,
  } = props;

  const innerOnClick = useCallback(() => {
    if (onClick) {
      onClick(page);
    }
  }, [page, onClick]);

  return (
    <Box as="li" mr={2}>
      <Button
        isDisabled={disabled}
        onClick={innerOnClick}
        data-testid={
          testId || `pagination-button-${page}${active ? '-active' : ''}`
        }
        {...(navButton ? paginationNavButtonStyles : paginationButtonStyles)(
          props
        )}
      >
        {children || page}
      </Button>
    </Box>
  );
}

const range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (v, k) => k + start);

export function Pagination({
  currentPage,
  maxPagesToShow,
  pageCount,
  onPageChange = () => {
    /* do nothing */
  },
}: PaginationProps) {
  const onPaginationButtonClick = useCallback(
    (page: number) => {
      onPageChange(page);
    },
    [onPageChange]
  );

  const {
    leftDots,
    rightDots,
    enabledPrevPage,
    enabledNextPage,
    toRenderPages,
  } = useMemo(() => {
    if (
      pageCount <= 1 ||
      pageCount === undefined ||
      currentPage === undefined
    ) {
      return {};
    }

    let leftDots = false;
    let rightDots = false;
    let navBegin = 1;
    let navEnd = maxPagesToShow;

    const middleMaxPagesToShow = Math.floor(maxPagesToShow / 2.0);

    if (pageCount > maxPagesToShow) {
      if (currentPage > middleMaxPagesToShow + 1) {
        leftDots = true;
        navBegin = currentPage - middleMaxPagesToShow + 1;
      }
      if (currentPage < pageCount - middleMaxPagesToShow) {
        rightDots = true;
        navEnd = navBegin + maxPagesToShow - (!leftDots ? 3 : 4);

        if (leftDots) {
          navBegin += 1;
        }
      } else {
        navEnd = pageCount;
        navBegin = pageCount - maxPagesToShow + 3;
      }
    } else {
      navEnd = pageCount;
    }

    if (pageCount === 0) {
      return null;
    }

    return {
      leftDots,
      rightDots,
      enabledPrevPage: currentPage > 1,
      enabledNextPage: currentPage < pageCount,
      toRenderPages: range(navBegin, navEnd),
    };
  }, [currentPage, pageCount, maxPagesToShow]);

  return pageCount > 1 ? (
    <Flex data-testid="pagination-container" as="nav" justifyContent="flex-end">
      <Flex as="ul" __css={{ display: 'flex', listStyle: 'none' }}>
        <PaginationButton
          disabled={!enabledPrevPage}
          page={currentPage - 1}
          navButton
          data-testid="prev-page-button"
          onClick={onPaginationButtonClick}
        >
          <Icon boxSize={5} icon={icons.fi.ChevronLeft} />
        </PaginationButton>

        {leftDots && (
          <>
            <PaginationButton page={1} onClick={onPaginationButtonClick} />
            <PaginationButton data-testid="pagination-ellipsis">
              ...
            </PaginationButton>
          </>
        )}

        {toRenderPages.map((page) => {
          return (
            <PaginationButton
              key={`pagination-button-${page}`}
              page={page}
              onClick={onPaginationButtonClick}
              active={page === currentPage}
            />
          );
        })}

        {rightDots && (
          <>
            <PaginationButton data-testid="pagination-ellipsis">
              ...
            </PaginationButton>
            <PaginationButton
              page={pageCount}
              onClick={onPaginationButtonClick}
            />
          </>
        )}

        <PaginationButton
          disabled={!enabledNextPage}
          page={currentPage + 1}
          navButton
          onClick={onPaginationButtonClick}
          data-testid="next-page-button"
        >
          <Icon boxSize={5} icon={icons.fi.ChevronRight} />
        </PaginationButton>
      </Flex>
    </Flex>
  ) : null;
}
